import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Eye, Trash2, Plus, ArrowUpDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import { Checkbox } from '@/components/ui/checkbox';
import ContratacaoFormTabs from '@/components/ContratacaoFormTabs';
import { SubscriptionLimitAlert } from '@/components/SubscriptionLimitAlert';
import { useSubscriptionLimits } from '@/hooks/useSubscriptionLimits';
interface ContratacaoItem {
  id: string;
  email: string;
  nomeTitular?: string;
  nome_completo?: string;
  position?: string;
  cargo?: string;
  cargo_id?: string;
  department?: string;
  stage: string;
  notes?: string;
  salario?: number;
  jornada_trabalho?: string;
  ids_modelos_contrato?: string[];
  created_at: string;
  updated_at: string;
  cpf?: string;
  hEntrada?: string;
  hSaida?: string;
  dataInicio?: string;
  modelo_proposta_id?: string;
  etapa_atual?: string;
  evolucao_contratacao?: { etapa_atual: string }[];
  tentativas_documentos?: number;
  todos_documentos_aprovados?: boolean;
  tem_documentos_reprovados?: boolean;
}

interface ModeloContrato {
  id: string;
  nome_modelo: string;
}

interface Cargo {
  id: string;
  nome: string;
}
export default function ContratacaoList() {
  const navigate = useNavigate();
  const {
    toast
  } = useToast();
  const {
    hasPermission,
    refetch
  } = usePermissions();
  const { canCreate: canCreateWithLimit, limitCount, currentCount, planName } = useSubscriptionLimits();
  const [contratacoes, setContratacoes] = useState<ContratacaoItem[]>([]);
  const [modelos, setModelos] = useState<ModeloContrato[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterStage, setFilterStage] = useState<string | undefined>(undefined);
  const [permissionsChecked, setPermissionsChecked] = useState(false);

  // Dialog state for creating new contratacao
  const [dialogOpen, setDialogOpen] = useState(false);
  const canRead = hasPermission('recursos_humanos', 'contratacao', 'read');
  const canWrite = hasPermission('recursos_humanos', 'contratacao', 'write');
  const canAdmin = hasPermission('recursos_humanos', 'contratacao', 'admin');

  const fetchContratacoes = async () => {
    try {
      setLoading(true);
      
      let query = supabase.from('contratacao').select('*');

      // Apply filters para email e nome
      if (filterEmail) {
        query = query.ilike('email', `%${filterEmail}%`);
      }
      if (filterName) {
        query = query.ilike('nome_completo', `%${filterName}%`);
      }

      // Apply sorting
      query = query.order(sortBy, { ascending: sortOrder === 'asc' });
      
      const { data: contratacaoData, error: contratacaoError } = await query;
      if (contratacaoError) throw contratacaoError;

      console.log('Dados de contratação base:', contratacaoData);

      // Buscar informações dos cargos separadamente
      const cargoIds = contratacaoData?.map(c => c.cargo_id).filter(id => id) || [];
      let cargosMap = new Map();
      
      if (cargoIds.length > 0) {
        const { data: cargosData } = await supabase
          .from('cargos')
          .select('id, nome')
          .in('id', cargoIds);
          
        cargosData?.forEach(cargo => {
          cargosMap.set(cargo.id, cargo.nome);
        });
      }

      // Primeiro buscar evolução diretamente pelos user_ids da tabela contratacao
      const userIds = contratacaoData?.map(c => c.id_usuario).filter(id => id) || [];
      let evolucaoMap = new Map();
      
      if (userIds.length > 0) {
        // Buscar evolução por user_ids diretamente
        const { data: evolucaoData } = await supabase
          .from('evolucao_contratacao')
          .select('user_id, etapa_mais_avancada')
          .in('user_id', userIds);
         
        // Criar mapa user_id -> etapa_mais_avancada  
        const userIdToEtapaMap = new Map();
        evolucaoData?.forEach(evo => {
          userIdToEtapaMap.set(evo.user_id, evo.etapa_mais_avancada);
        });
         
        // Mapear etapas baseado no id_usuario da contratacao
        contratacaoData?.forEach(contratacao => {
          if (contratacao.id_usuario) {
            const etapa = userIdToEtapaMap.get(contratacao.id_usuario);
            if (etapa) {
              evolucaoMap.set(contratacao.email, etapa);
            }
          }
        });
      }

      // Fallback: buscar através do email usando profiles para contratações sem id_usuario
      const emails = contratacaoData?.filter(c => !c.id_usuario).map(c => c.email).filter(email => email) || [];
      
      if (emails.length > 0) {
        // Buscar user_ids através dos emails na tabela profiles
        const { data: profilesData } = await supabase
          .from('profiles')
          .select('user_id, email')
          .in('email', emails);
          
        const userIdsFromProfiles = profilesData?.map(p => p.user_id) || [];
        
        if (userIdsFromProfiles.length > 0) {
          // Buscar evolução por user_ids
          const { data: evolucaoData } = await supabase
            .from('evolucao_contratacao')
            .select('user_id, etapa_mais_avancada')
            .in('user_id', userIdsFromProfiles);
           
          // Criar um mapa email -> user_id
          const emailToUserIdMap = new Map();
          profilesData?.forEach(p => {
            emailToUserIdMap.set(p.email, p.user_id);
          });
           
          // Criar mapa user_id -> etapa_mais_avancada  
          const userIdToEtapaMap = new Map();
          evolucaoData?.forEach(evo => {
            userIdToEtapaMap.set(evo.user_id, evo.etapa_mais_avancada);
          });
           
          // Criar mapa final email -> etapa_mais_avancada
          emailToUserIdMap.forEach((userId, email) => {
            const etapa = userIdToEtapaMap.get(userId);
            if (etapa) {
              evolucaoMap.set(email, etapa);
            }
          });
        }
      }

      // Buscar informações de documentos para verificar se foram aprovados
      const { data: documentosData } = await supabase
        .from('documentos_contratacao')
        .select('cpf, tipo_documento, status_validacao');

      // Criar mapa de documentos por CPF
      const documentosMap = new Map();
      documentosData?.forEach(doc => {
        if (!documentosMap.has(doc.cpf)) {
          documentosMap.set(doc.cpf, []);
        }
        documentosMap.get(doc.cpf).push(doc);
      });

      // Mapear os dados incluindo etapa_atual e status dos documentos
      let contratacoesMapped = (contratacaoData || []).map((contratacao: any) => {
        // SEMPRE usar a evolucao_contratacao como fonte principal de etapa
        // O campo 'stage' da tabela contratacao está desatualizado
        // Agora usando etapa_mais_avancada ao invés de etapa_atual
        const etapaAtual = evolucaoMap.get(contratacao.email) || null;
        const cargoNome = cargosMap.get(contratacao.cargo_id) || null;
        
        // Verificar se todos os documentos foram aprovados
        const documentos = documentosMap.get(contratacao.cpf) || [];
        const temDocumentosReprovados = documentos.some(doc => doc.status_validacao === 'reprovado');
        const todosDocumentosAprovados = documentos.length > 0 && documentos.every(doc => doc.status_validacao === 'aprovado');
        
        console.log(`🔍 Mapeando etapa para ${contratacao.email}:`, {
          etapaMaisAvancada: evolucaoMap.get(contratacao.email),
          stageContratacao: contratacao.stage,
          etapaFinal: etapaAtual
        });
        
        return {
          ...contratacao,
          etapa_atual: etapaAtual,
          cargo_nome: cargoNome,
          todos_documentos_aprovados: todosDocumentosAprovados,
          tem_documentos_reprovados: temDocumentosReprovados
        };
      });

      // Aplicar filtro de stage após obter os dados de evolução
      if (filterStage) {
        contratacoesMapped = contratacoesMapped.filter((contratacao: any) => 
          contratacao.etapa_atual === filterStage
        );
      }

      console.log('Dados finais processados:', contratacoesMapped);
      setContratacoes(contratacoesMapped);
    } catch (error: any) {
      console.error('Erro ao buscar contratações:', error);
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchModelos = async () => {
    try {
      const { data, error } = await supabase
        .from('tbl_modelo_contrato')
        .select('id, nome_modelo')
        .eq('status', 'ativo');

      if (error) throw error;

      setModelos(data || []);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar modelos",
        description: error.message,
        variant: "destructive",
      });
    }
  };


  useEffect(() => {
    refetch().then(() => {
      setPermissionsChecked(true);
    });
  }, []);

  useEffect(() => {
    if (!permissionsChecked) return;
    if (!canRead) {
      navigate('/intranet');
      return;
    }
    fetchContratacoes();
    fetchModelos();
  }, [canRead, permissionsChecked, sortBy, sortOrder, filterEmail, filterName, filterStage]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };
  const handleDelete = async (id: string) => {
    try {
      // 1. Excluir propostas relacionadas
      await supabase
        .from('contratacao_propostas')
        .delete()
        .eq('contratacao_id', id);

      // 2. Excluir contratos relacionados
      await supabase
        .from('contratacao_contratos')
        .delete()
        .eq('contratacao_id', id);

      // 3. Excluir tentativas de documentos
      await supabase
        .from('contratacao_tentativas_documentos')
        .delete()
        .eq('contratacao_id', id);

      // 4. Excluir a contratação e verificar se realmente deletou
      const { data, error } = await supabase
        .from('contratacao')
        .delete()
        .eq('id', id)
        .select();

      if (error) throw error;

      // Verificar se realmente deletou algum registro
      if (!data || data.length === 0) {
        throw new Error('Não foi possível excluir a contratação. Verifique se você tem permissão ou se o registro existe.');
      }

      toast({
        title: "Sucesso",
        description: "Contratação excluída com sucesso"
      });
      fetchContratacoes();
    } catch (error: any) {
      toast({
        title: "Erro ao excluir",
        description: error.message,
        variant: "destructive"
      });
    }
  };
  const formatCurrency = (value?: number) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };
  const getModelosNames = (ids?: string[]) => {
    if (!ids || ids.length === 0) return '-';
    return ids.map(id => {
      const modelo = modelos.find(m => m.id === id);
      return modelo ? modelo.nome_modelo : 'Modelo não encontrado';
    }).join(', ');
  };
  if (!permissionsChecked || loading) {
    return <div className="flex justify-center p-8">Carregando...</div>;
  }
  return <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Contratação</h1>
        {canWrite && <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button disabled={!canCreateWithLimit}>
                <Plus className="mr-2 h-4 w-4" />
                Criar Contratação
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Nova Contratação</DialogTitle>
              </DialogHeader>
              <ContratacaoFormTabs 
                onClose={() => setDialogOpen(false)}
                onSuccess={() => fetchContratacoes()}
              />
            </DialogContent>
          </Dialog>}
      </div>

      {/* Subscription Limit Alert */}
      <SubscriptionLimitAlert />

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="filter-email">Filtrar por Email</Label>
              <Input id="filter-email" value={filterEmail} onChange={e => setFilterEmail(e.target.value)} placeholder="Digite o email..." />
            </div>
            <div>
              <Label htmlFor="filter-name">Filtrar por Nome</Label>
              <Input id="filter-name" value={filterName} onChange={e => setFilterName(e.target.value)} placeholder="Digite o nome..." />
            </div>
            <div>
              <Label htmlFor="filter-stage">Filtrar por Estágio</Label>
              <div className="flex space-x-2">
                <Select value={filterStage || ""} onValueChange={value => setFilterStage(value || undefined)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os estágios" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="proposta">Proposta</SelectItem>
                    <SelectItem value="dados_pessoais">Dados Pessoais</SelectItem>
                    <SelectItem value="documentos">Documentos</SelectItem>
                    <SelectItem value="validacao_documentos">Validação de Documentos</SelectItem>
                    <SelectItem value="validacao">Validação</SelectItem>
                    <SelectItem value="exame_admissional">Exame Admissional</SelectItem>
                    <SelectItem value="exame">Exame</SelectItem>
                    <SelectItem value="contrato">Contrato</SelectItem>
                    <SelectItem value="integracao">Integração</SelectItem>
                    <SelectItem value="finalizado">Finalizado</SelectItem>
                  </SelectContent>
                </Select>
                {filterStage && <Button variant="outline" size="sm" onClick={() => setFilterStage(undefined)}>
                    Limpar
                  </Button>}
              </div>
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setFilterEmail('');
                setFilterName('');
                setFilterStage(undefined);
              }}
            >
              Limpar Todos os Filtros
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Contratações</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Cargo</TableHead>
                <TableHead>Salário</TableHead>
                <TableHead>Jornada</TableHead>
                <TableHead>Estágio</TableHead>
                <TableHead>Modelos de Contrato</TableHead>
                <TableHead>Criado em</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contratacoes.length === 0 ? <TableRow>
                     <TableCell colSpan={9} className="text-center">
                       Nenhuma contratação encontrada
                     </TableCell>
                   </TableRow> : contratacoes.map(contratacao => <TableRow key={contratacao.id}>
                     <TableCell>{contratacao.email}</TableCell>
                      <TableCell>{contratacao.nome_completo || contratacao.nomeTitular}</TableCell>
                      <TableCell>{(contratacao as any).cargo_nome || contratacao.cargo || '-'}</TableCell>
                      <TableCell>{formatCurrency(contratacao.salario)}</TableCell>
                     <TableCell>{contratacao.jornada_trabalho || '-'}</TableCell>
                       <TableCell>
                          {(() => {
                            const etapa = (contratacao as any).etapa_atual;
                            
                            // Se não há etapa definida, não exibir nada
                            if (!etapa) {
                              return (
                                <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-800">
                                  Não iniciado
                                </span>
                              );
                            }
                            
                            const etapaFormatada = (() => {
                              switch(etapa) {
                                case 'dados_pessoais': return 'Dados Pessoais';
                                case 'documentos': return 'Documentos';
                                case 'validacao_documentos': return 'Validação de Documentos';
                                case 'validacao': return 'Validação';
                                case 'exame_admissional': return 'Exame Admissional';
                                case 'exame': return 'Exame';
                                case 'contrato': return 'Contrato';
                                case 'assinatura_contrato': return 'Assinatura de Contrato';
                                case 'integracao': return 'Integração';
                                case 'finalizado': return 'Finalizado';
                                case 'proposta': return 'Proposta';
                                // Mapeamento para valores legados da tabela contratacao.stage
                                case 'proposta_enviada': return 'Proposta Enviada (Legado)';
                                case 'inicial': return 'Inicial (Legado)';
                                default: return etapa || 'Pendente';
                              }
                            })();
                           
                            const getEtapaColor = (etapa: string) => {
                              switch(etapa) {
                                case 'dados_pessoais':
                                case 'proposta':
                                  return 'bg-blue-100 text-blue-800';
                                case 'documentos':
                                case 'validacao_documentos':
                                case 'validacao':
                                  return 'bg-yellow-100 text-yellow-800';
                                case 'exame_admissional':
                                case 'exame':
                                  return 'bg-orange-100 text-orange-800';
                                case 'contrato':
                                case 'assinatura_contrato':
                                case 'integracao':
                                  return 'bg-purple-100 text-purple-800';
                                case 'finalizado':
                                  return 'bg-green-100 text-green-800';
                                default:
                                  return 'bg-gray-100 text-gray-800';
                              }
                            };
                          
                          return (
                            <span className={`px-2 py-1 rounded text-xs ${getEtapaColor(etapa)}`}>
                              {etapaFormatada}
                            </span>
                          );
                        })()}
                      </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {getModelosNames(contratacao.ids_modelos_contrato)}
                    </TableCell>
                    <TableCell>
                      {new Date(contratacao.created_at).toLocaleDateString('pt-BR')}
                    </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button variant="outline" size="sm" onClick={() => navigate(`/intranet/contratacao/${contratacao.id}/view`)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                         {canWrite && <Button variant="outline" size="sm" onClick={() => navigate(`/intranet/contratacao/${contratacao.id}/edit`)}>
                             <Edit className="h-4 w-4" />
                           </Button>}
                         {canAdmin && <AlertDialog>
                             <AlertDialogTrigger asChild>
                               <Button variant="outline" size="sm">
                                 <Trash2 className="h-4 w-4" />
                               </Button>
                             </AlertDialogTrigger>
                             <AlertDialogContent>
                               <AlertDialogHeader>
                                 <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                                 <AlertDialogDescription>
                                   Tem certeza que deseja excluir esta contratação? Esta ação não pode ser desfeita.
                                 </AlertDialogDescription>
                               </AlertDialogHeader>
                               <AlertDialogFooter>
                                 <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                 <AlertDialogAction onClick={() => handleDelete(contratacao.id)}>
                                   Excluir
                                 </AlertDialogAction>
                               </AlertDialogFooter>
                             </AlertDialogContent>
                           </AlertDialog>}
                           {/* Ícone de alerta - só exibe se há documentos reprovados E não foram todos aprovados */}
                           {contratacao.tem_documentos_reprovados && !contratacao.todos_documentos_aprovados && (
                             <div className="flex items-center justify-center w-8 h-8 bg-yellow-100 rounded-full" title="Há documentos reprovados que precisam de atenção">
                               <svg className="w-4 h-4 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                                 <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                               </svg>
                             </div>
                           )}
                       </div>
                     </TableCell>
                  </TableRow>)}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>;
}